import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { fetchWithCache } from '../lib/fetchWithCache';
import { SoilCarbonStats } from '../lib/types';

interface UseSoilCarbonResult {
  data: SoilCarbonStats | null;
  isLoading: boolean;
  error: string | null;
}

// Pulls `${mill}_${driveTime}min` out of the file column, e.g.
// `s3://.../mill_shapes/alabama-coastal-plain_60min.geojson` → `alabama-coastal-plain_60`.
const KEY_FROM_FILE = /([a-z-]+)_(60|90)min\.geojson$/;

const buildSoilCarbonTable = (
  text: string,
  depth: 30 | 100,
): Map<string, SoilCarbonStats> => {
  const table = new Map<string, SoilCarbonStats>();
  for (const row of parseCsv(text)) {
    const file = row['file'] ?? '';
    const match = file.match(KEY_FROM_FILE);
    if (!match) continue;
    table.set(`${match[1]}_${match[2]}`, {
      depthCm: depth,
      mean: Number(row['mean']),
      std: Number(row['std']),
      min: Number(row['min']),
      max: Number(row['max']),
    });
  }
  return table;
};

/**
 * Fetches the per-depth SoC summary table once (cached), then selects the
 * row matching (millCode, driveTime). The hook owns the row selection so
 * consumers can't forget to re-derive when the drive-time toggle flips.
 */
export const useSoilCarbon = (
  depth: 30 | 100,
  millCode: string | null,
  driveTimeMinutes: number,
  enabled: boolean = true,
): UseSoilCarbonResult => {
  const [data, setData] = useState<SoilCarbonStats | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.soilCarbon(depth)}`;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache(url, r =>
      r.text().then(text => buildSoilCarbonTable(text, depth)),
    )
      .then(table => {
        if (cancelled) return;
        const row = table.get(`${millCode}_${driveTimeMinutes}`);
        setData(row ?? null);
        setIsLoading(false);
        if (!row) {
          setError(`No SoC row for ${millCode} at ${driveTimeMinutes}min`);
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(`Failed to load soil carbon: ${err.message}`);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [depth, millCode, driveTimeMinutes, enabled]);

  return { data, isLoading, error };
};
